// Répertoire et carte nationale (M24, lot L6-A) : collectivités du périmètre institutionnel, état de
// déploiement, population déclarée ; carte existante (limites OpenStreetMap, échelles Gabon et provinces),
// marqueurs au centre de la limite rapprochée, jamais à une position inventée.
import { Bandeau, CarteTerritoire, EtatVide, PuceEtat, Squelette, Tableau, type ColonneTableau } from '@cc/ui';
import { useMemo, useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useLimites } from '../territoire/donnees';
import { useRepertoire, type Filtres, type LigneRepertoire } from './api';
import { FiltresTerritoire, NavigationSupervision, provincesDu } from './Communs';
import { echellesNationales, LIBELLES_DEPLOIEMENT, LIBELLES_TYPE, marqueursCollectivites, PERIMETRES, TON_DEPLOIEMENT } from './logique';
import styles from '../achats/achats.module.css';
import propres from './supervision.module.css';

const FICHIERS = ['pays', 'provinces', 'communes', 'departements'] as const;

export function PageRepertoire() {
  const [filtres, setFiltres] = useState<Filtres>({});
  const repertoire = useRepertoire(filtres);
  const tout = useRepertoire({});
  const { limites, presentation, chargement, erreur } = useLimites(FICHIERS);
  const lignes = repertoire.data?.elements ?? [];
  const { provinces, imposee } = provincesDu(tout.data?.perimetre, tout.data?.elements ?? []);
  const echelles = useMemo(() => (chargement ? null : echellesNationales(limites)), [chargement, limites]);
  const carte = useMemo(() => marqueursCollectivites(lignes, limites, presentation), [lignes, limites, presentation]);
  const [actif, setActif] = useState<string | null>(null);
  const echelle = filtres.province ?? imposee ?? 'gabon';
  const colonnes: ColonneTableau<LigneRepertoire>[] = [
    { id: 'nom', entete: 'Collectivité', enTeteLigne: true, masquable: false, cellule: (l) => `${l.nom}${l.fictive ? ' (fictive)' : ''}`, valeurTri: (l) => l.nom },
    { id: 'type', entete: 'Type', cellule: (l) => LIBELLES_TYPE[l.type] ?? l.type },
    { id: 'province', entete: 'Province', cellule: (l) => l.province_nom, valeurTri: (l) => l.province_code },
    { id: 'departement', entete: 'Département', cellule: (l) => l.departement_nom ?? 'non renseigné' },
    { id: 'statut', entete: 'Déploiement', cellule: (l) => <PuceEtat ton={TON_DEPLOIEMENT[l.statut_deploiement] ?? 'neutre'} libelle={LIBELLES_DEPLOIEMENT[l.statut_deploiement] ?? l.statut_deploiement} /> },
    { id: 'population', entete: 'Population déclarée', numerique: true, cellule: (l) => (l.population_declaree === null ? 'non déclarée' : l.population_declaree.toLocaleString('fr-FR')), valeurTri: (l) => l.population_declaree },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Supervision"
        titre="Répertoire des collectivités"
        description={`Collectivités de votre périmètre ${PERIMETRES[repertoire.data?.perimetre ?? ''] ?? ''}, état de déploiement et population déclarée. Agrégats seulement : aucun dossier, agent ni usager.`}
      />
      <NavigationSupervision actif="repertoire" />
      <FiltresTerritoire filtres={filtres} onChange={setFiltres} provinces={provinces} imposee={imposee} />
      <EtatRequete chargement={repertoire.isPending} erreur={repertoire.error} onReessayer={() => void repertoire.refetch()} lot="L6-A" fonction="Le répertoire de supervision">
        <section className={styles.bloc} aria-labelledby="titre-carte">
          <h2 id="titre-carte">Carte</h2>
          {erreur ? <Bandeau type="erreur">Les limites administratives n&apos;ont pas pu être chargées : la carte s&apos;affiche sans contour.</Bandeau> : null}
          {carte.exemples.length > 0 ? <Bandeau type="info">Collectivités fictives de présentation placées sur un territoire d&apos;exemple : {carte.exemples.join(', ')}.</Bandeau> : null}
          {carte.sansPosition.length > 0 ? <p className={propres.mention}>Sans limite connue, donc sans marqueur : {carte.sansPosition.join(', ')}.</p> : null}
          {echelles ? (
            <CarteTerritoire
              titre="Carte des collectivités"
              echelles={echelles}
              echelle={echelles.some((e) => e.id === echelle) ? echelle : 'gabon'}
              onEchelle={(id) => (imposee ? undefined : setFiltres((f) => ({ ...f, province: id === 'gabon' ? undefined : id })))}
              marqueurs={carte.marqueurs}
              marqueurActif={actif}
              onMarqueur={setActif}
              libellesTon={{ ouvert: 'Active', retard: 'Suspendue', autre: 'Autre état de déploiement' }}
              libelleObjets={{ singulier: 'collectivité placée', pluriel: 'collectivités placées' }}
              hauteur="min(60vh, 520px)"
            />
          ) : (
            <Squelette hauteurBloc={360} libelle="Chargement de la carte" />
          )}
        </section>
        <section className={styles.bloc} aria-labelledby="titre-repertoire">
          <h2 id="titre-repertoire">Répertoire ({lignes.length})</h2>
          {lignes.length === 0 ? (
            <EtatVide titre="Aucune collectivité">Aucune collectivité ne correspond à ces filtres dans votre périmètre.</EtatVide>
          ) : (
            <Tableau legende="Répertoire des collectivités" colonnes={colonnes} lignes={lignes} cleLigne={(l) => l.collectivite_id} cleColonnes="supervision-repertoire" />
          )}
        </section>
      </EtatRequete>
    </>
  );
}
